import React, { useState, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CheckSquare, ArrowRight, Loader, AlertCircle } from "lucide-react";
import { supabase, testSupabaseConnection } from "../lib/supabase";

interface AssessmentCardProps {
  onContactClick?: () => void;
}

export default function AssessmentCard({
  onContactClick,
}: AssessmentCardProps) {
  const [surveyId, setSurveyId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<boolean | null>(
    null,
  );
  const [retryCount, setRetryCount] = useState(0);
  const navigate = useNavigate();
  const maxRetries = 3;

  // Memoize the connection check function to prevent recreation
  const checkConnection = useMemo(
    () => async () => {
      try {
        setLoading(true);
        setError(null);

        // Test connection first
        const isConnected = await testSupabaseConnection();
        setConnectionStatus(isConnected);

        if (!isConnected) {
          throw new Error(
            "Unable to connect to Supabase. Please check your configuration.",
          );
        }

        // Fetch active survey
        const id = await getFirstActiveSurvey();
        if (id) {
          setSurveyId(id);
        } else {
          setError("No active surveys found");
          setConnectionStatus(true);
        }
      } catch (error) {
        console.error("Error fetching survey ID:", error);
        setError(
          `Error fetching survey ID: ${error instanceof Error ? error.message : "Failed to load survey"}`,
        );

        // Implement retry logic
        if (retryCount < maxRetries) {
          setTimeout(() => {
            setRetryCount((prev) => prev + 1);
          }, 2000); // Wait 2 seconds before retrying
        }
      } finally {
        setLoading(false);
      }
    },
    [retryCount],
  );

  useEffect(() => {
    checkConnection();
  }, [checkConnection]);

  async function getFirstActiveSurvey(): Promise<string | null> {
    try {
      const { data, error } = await supabase
        .from("surveys")
        .select("id")
        .eq("is_active", true)
        .order("created_at", { ascending: false })
        .limit(1)
        .single();

      if (error) {
        console.error("Error fetching first active survey:", error);
        return null;
      }

      return data?.id || null;
    } catch (error) {
      console.error("Error in getFirstActiveSurvey:", error);
      return null;
    }
  }

  // Memoize the component content to prevent unnecessary re-renders
  const cardContent = useMemo(
    () => (
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-all duration-200">
        <div className="flex items-center p-5">
          <div className="bg-black rounded-full p-3 mr-4 flex-shrink-0">
            <CheckSquare className="h-5 w-5 text-white" />
          </div>
          <div className="flex-grow">
            <h3 className="text-lg font-semibold text-gray-900">
              Is Your Business Ready for a Fractional PM?
            </h3>
            <p className="text-sm text-gray-600">
              Get a Custom Fractional PM Strategy for Your Business
            </p>
          </div>
          {loading ? (
            <div className="flex-shrink-0 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-gray-400 animate-pulse">
              <Loader className="h-4 w-4 animate-spin mr-2" />
              Loading
            </div>
          ) : error ? (
            <div className="flex-shrink-0 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-500">
              <AlertCircle className="h-4 w-4 mr-1" />
              Error
            </div>
          ) : (
            <Link
              to="/fractional-pm"
              className="flex-shrink-0 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-black hover:bg-gray-800 transition-colors duration-200"
            >
              Learn More
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          )}
        </div>
        {error && (
          <div className="px-5 pb-4">
            <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded">
              <div className="flex">
                <div className="flex-shrink-0">
                  <AlertCircle className="h-5 w-5 text-red-400" />
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-700">{error}</p>
                  {!connectionStatus && (
                    <div className="mt-2">
                      <p className="text-sm font-medium text-red-700">
                        Troubleshooting Steps:
                      </p>
                      <ol className="mt-1 text-sm text-red-700 list-decimal list-inside">
                        <li>
                          Verify your Supabase URL and anon key in the .env file
                        </li>
                        <li>
                          Check if your Supabase project is online and
                          accessible
                        </li>
                        <li>
                          Ensure the database tables have been created correctly
                        </li>
                        <li>
                          Verify that Row Level Security (RLS) policies are
                          properly configured
                        </li>
                      </ol>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    ),
    [loading, error, connectionStatus],
  );

  return cardContent;
}
